import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { debounce } from 'lodash';
import { citCrowdsaleContract } from 'shared/config/wallet-service/contracts';
import { toDecimals } from 'shared/lib';

import { wusdtToken } from '../../model/sale-token';
import { formatEstimateResult, InvalidAmountError, parseEstimateError } from '../lib/estimate';
import { DebouncedEstimateState, EstimateOptions, EstimateResult } from '../types';

interface UseDebouncedEstimateOptions extends EstimateOptions {
  debounceMs?: number;
  minPaymentAmount?: bigint;
}

type EstimateState = Omit<DebouncedEstimateState, 'isError'>;

type EstimateAction =
  | { type: 'START_DEBOUNCE' }
  | { type: 'START_LOADING' }
  | { type: 'SUCCESS'; data: EstimateResult }
  | { type: 'ERROR'; error: DebouncedEstimateState['error'] }
  | { type: 'RESET' };

const initialState: EstimateState = {
  data: null,
  error: null,
  isLoading: false,
  isDebouncing: false,
};

function estimateReducer(state: EstimateState, action: EstimateAction): EstimateState {
  switch (action.type) {
    case 'START_DEBOUNCE':
      return { ...initialState, isDebouncing: true };
    case 'START_LOADING':
      return { ...state, isLoading: true, isDebouncing: false };
    case 'SUCCESS':
      return { ...initialState, data: action.data };
    case 'ERROR':
      return { ...initialState, error: action.error };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export function useDebouncedEstimate(options: UseDebouncedEstimateOptions): DebouncedEstimateState {
  const { paymentAmount, enabled = true, debounceMs = 650 } = options;

  const [state, dispatch] = useReducer(estimateReducer, initialState);

  const isEnabled = useMemo(() => {
    return enabled && paymentAmount !== '';
  }, [enabled, paymentAmount]);

  const performEstimate = useCallback(async (amount: string) => {
    dispatch({ type: 'START_LOADING' });

    const amountWithDecimals = BigInt(toDecimals(amount, wusdtToken.decimals));

    try {
      const result = await citCrowdsaleContract.read.estimate([amountWithDecimals]);
      const isMaxAmountOverflow = result[result.length - 1] === BigInt(0);

      if (isMaxAmountOverflow) {
        throw new InvalidAmountError({
          kind: 'TOO_HIGH',
          providedAmount: amountWithDecimals,
          decimals: wusdtToken.decimals,
        });
      }

      const formattedResult = formatEstimateResult(result);

      dispatch({ type: 'SUCCESS', data: formattedResult });
    } catch (err) {
      console.error('[performEstimate] error', err);
      const parsedError = parseEstimateError(err, amountWithDecimals);
      dispatch({ type: 'ERROR', error: parsedError });
    }
  }, []);

  const debouncedEstimate = useMemo(
    () =>
      debounce((amount: string) => {
        performEstimate(amount);
      }, debounceMs),
    [performEstimate, debounceMs],
  );

  useEffect(() => {
    if (!isEnabled) {
      dispatch({ type: 'RESET' });
      return;
    }

    dispatch({ type: 'START_DEBOUNCE' });
    debouncedEstimate(paymentAmount);

    // eslint-disable-next-line consistent-return
    return () => {
      debouncedEstimate.cancel();
    };
  }, [isEnabled, paymentAmount, debouncedEstimate]);

  return useMemo(
    () => ({
      ...state,
      isLoading: state.isLoading || state.isDebouncing,
      isError: !!state.error,
    }),
    [state],
  );
}
